/**
 * MaterialRestrictionsCard Component
 *
 * Displays prohibited items for the selected material type
 * and potential contamination fees. Uses warning colors (amber/yellow)
 * to draw attention to restrictions without being alarming.
 */

import { AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { formatCurrency } from '../../utils/formatCurrency';

export interface MaterialRestrictionsCardProps {
  materialType: string;
  prohibitedItems: string[];
  contaminationFeeRange?: { min: number; max: number };
}

export function MaterialRestrictionsCard({
  materialType,
  prohibitedItems,
  contaminationFeeRange,
}: MaterialRestrictionsCardProps) {
  const getMaterialLabel = (type: string): string => {
    const labels: Record<string, string> = {
      construction_debris: 'C&D',
      mixed_cd: 'Mixed C&D',
      concrete: 'Concrete',
      dirt: 'Dirt/Soil',
      general_waste: 'General Waste',
      green_waste: 'Green Waste',
    };
    return labels[type] || type;
  };

  if (!prohibitedItems || prohibitedItems.length === 0) {
    return null;
  }

  return (
    <Card className="border-amber-200 bg-amber-50/30">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2 text-amber-900">
          <AlertTriangle className="h-5 w-5" aria-hidden="true" />
          {getMaterialLabel(materialType)} Material Restrictions
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <p className="text-sm font-medium text-gray-900 mb-2">
            The following items CANNOT go in your container:
          </p>
          <ul className="space-y-1">
            {prohibitedItems.slice(0, 5).map((item, i) => (
              <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                <span className="text-amber-600" aria-hidden="true">
                  •
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
          {prohibitedItems.length > 5 && (
            <p className="text-xs text-gray-600 mt-2 italic">
              + {prohibitedItems.length - 5} more restricted items
            </p>
          )}
        </div>

        {contaminationFeeRange && (
          <div className="pt-2 border-t border-amber-200">
            <p className="text-sm font-medium text-amber-900">
              <span aria-hidden="true">⚡</span> Contamination fee:{' '}
              {formatCurrency(contaminationFeeRange.min)}-
              {formatCurrency(contaminationFeeRange.max)}
            </p>
            <p className="text-xs text-gray-600 mt-1">
              Charged if prohibited materials are found during processing
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
